import Phaser from 'phaser';
import { TILE_WIDTH, TILE_HEIGHT, worldToScreen, screenToWorld } from '../utils/IsometricUtils';

interface TileInfo {
  id: number;
  name: string;
  color: number;
}

interface MapObject {
  id: number;
  name: string;
  type: string;
  x: number;  // World tile X
  y: number;  // World tile Y
  width: number;
  height: number;
}

/**
 * MapEditorScene - Simple isometric map editor
 * Features:
 * - Click to place tiles
 * - Tile palette for selection
 * - Layer switching (ground, paths, collision)
 * - Save/Export functionality
 */
export class MapEditorScene extends Phaser.Scene {
  private mapWidth = 40;
  private mapHeight = 40;
  private mapData: { ground: number[]; paths: number[]; collision: number[] };
  private mapObjects: MapObject[] = [];
  private nextObjectId = 1;

  private currentLayer: 'ground' | 'paths' | 'collision' | 'objects' = 'ground';
  private selectedTile = 1;
  private selectedObjectType: 'player_spawn' | 'shrine' | 'delete' = 'shrine';

  private tileGraphics!: Phaser.GameObjects.Graphics;
  private objectGraphics!: Phaser.GameObjects.Graphics;
  private cursorGraphics!: Phaser.GameObjects.Graphics;
  private uiContainer!: Phaser.GameObjects.Container;

  private offsetX = 640;
  private offsetY = 100;

  private isDragging = false;
  private lastDragTile = { x: -1, y: -1 };
  private zoomLevel = 1;
  private isPanning = false;
  private lastPanPoint = { x: 0, y: 0 };
  private showCollisionOverlay = true;

  /** Visual tiles for ground/paths layers */
  private visualTiles: TileInfo[] = [
    { id: 0, name: 'Empty', color: 0x000000 },
    { id: 1, name: 'Border', color: 0x4a5568 },
    { id: 2, name: 'Grass', color: 0x3d7a5f },
    { id: 3, name: 'Path', color: 0xd4a574 },
  ];

  /** Collision tiles - these define gameplay collision, not visuals */
  private collisionTiles: TileInfo[] = [
    { id: 0, name: 'Empty', color: 0x222222 },
    { id: 1, name: 'Block', color: 0xef4444 },    // Blocks movement
    { id: 4, name: 'Trigger', color: 0xfbbf24 },  // Triggers events, allows movement
  ];

  constructor() {
    super({ key: 'MapEditorScene' });

    // Initialize empty map data
    this.mapData = {
      ground: new Array(this.mapWidth * this.mapHeight).fill(2), // Default to grass
      paths: new Array(this.mapWidth * this.mapHeight).fill(0),
      collision: new Array(this.mapWidth * this.mapHeight).fill(0),
    };
  }

  create(): void {
    // Reset map data for fresh load
    this.mapData = {
      ground: new Array(this.mapWidth * this.mapHeight).fill(2),
      paths: new Array(this.mapWidth * this.mapHeight).fill(0),
      collision: new Array(this.mapWidth * this.mapHeight).fill(0),
    };
    this.mapObjects = [];
    this.nextObjectId = 1;

    // Try to load existing map data
    this.loadExistingMap();

    // Create graphics objects with initial position
    this.tileGraphics = this.add.graphics();
    this.tileGraphics.setPosition(this.offsetX, this.offsetY);

    this.objectGraphics = this.add.graphics();
    this.objectGraphics.setPosition(this.offsetX, this.offsetY);
    this.objectGraphics.setDepth(10); // Above tiles

    this.cursorGraphics = this.add.graphics();
    this.cursorGraphics.setPosition(this.offsetX, this.offsetY);
    this.cursorGraphics.setDepth(20); // Above objects

    // Draw initial map
    this.renderMap();
    this.renderObjects();

    // Create UI
    this.createUI();

    // Set up input
    this.setupInput();

    // Instructions
    const instructions = this.add.text(10, 10, 'Map Editor - Click: paint | Right drag: pan | Scroll: zoom | G/P/C/O: layers', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#ffffff',
    });
    instructions.setDepth(1000);
  }

  private loadExistingMap(): void {
    const mapJson = this.cache.json.get('meadowvale_test');
    if (!mapJson) return;

    this.mapWidth = mapJson.width || 40;
    this.mapHeight = mapJson.height || 40;

    // Load layer data
    for (const layer of mapJson.layers || []) {
      if (layer.type === 'tilelayer' && layer.data) {
        if (layer.name === 'ground') {
          this.mapData.ground = [...layer.data];
        } else if (layer.name === 'paths') {
          this.mapData.paths = [...layer.data];
        } else if (layer.name === 'collision') {
          this.mapData.collision = [...layer.data];
        }
      } else if (layer.type === 'objectgroup' && layer.objects) {
        // Load objects (spawns, shrines, etc.)
        for (const obj of layer.objects) {
          // Convert Tiled pixel coords to world tile coords
          const worldCoords = this.tiledPixelToWorld(obj.x || 0, obj.y || 0);

          this.mapObjects.push({
            id: obj.id || this.nextObjectId++,
            name: obj.name || 'unnamed',
            type: obj.type || 'spawn',
            x: worldCoords.x,
            y: worldCoords.y,
            width: (obj.width || 64) / 64, // Convert pixel width to tiles
            height: (obj.height || 32) / 32,
          });

          // Track highest ID
          if (obj.id >= this.nextObjectId) {
            this.nextObjectId = obj.id + 1;
          }
        }
      }
    }
  }

  /**
   * Convert Tiled pixel coordinates to world tile coordinates
   */
  private tiledPixelToWorld(pixelX: number, pixelY: number): { x: number; y: number } {
    const halfWidth = TILE_WIDTH / 2;
    const halfHeight = TILE_HEIGHT / 2;
    return {
      x: (pixelX / halfWidth + pixelY / halfHeight) / 2,
      y: (pixelY / halfHeight - pixelX / halfWidth) / 2,
    };
  }

  /**
   * Convert world tile coordinates to Tiled pixel coordinates
   */
  private worldToTiledPixel(worldX: number, worldY: number): { x: number; y: number } {
    const halfWidth = TILE_WIDTH / 2;
    const halfHeight = TILE_HEIGHT / 2;
    return {
      x: (worldX - worldY) * halfWidth,
      y: (worldX + worldY) * halfHeight,
    };
  }

  private createUI(): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    this.uiContainer = this.add.container(width - 200, 50);
    this.uiContainer.setDepth(1000);

    // Panel background
    const panel = this.add.graphics();
    panel.fillStyle(0x1a1a2e, 0.9);
    panel.fillRect(0, 0, 190, height - 100);
    panel.lineStyle(2, 0x4ade80);
    panel.strokeRect(0, 0, 190, height - 100);
    this.uiContainer.add(panel);

    // Title
    const title = this.add.text(95, 15, 'TILE PALETTE', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#4ade80',
    }).setOrigin(0.5);
    this.uiContainer.add(title);

    // Layer selector
    const layerLabel = this.add.text(10, 45, 'Layer:', {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#ffffff',
    });
    this.uiContainer.add(layerLabel);

    const layers = ['ground', 'paths', 'collision', 'objects'] as const;
    layers.forEach((layer, i) => {
      const label = layer === 'objects' ? 'Obj' : layer.charAt(0).toUpperCase() + layer.slice(1, 3);
      const btn = this.add.text(10 + i * 45, 65, label, {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: this.currentLayer === layer ? '#4ade80' : '#888888',
        backgroundColor: this.currentLayer === layer ? '#2d5a47' : '#333333',
        padding: { x: 3, y: 2 },
      }).setInteractive({ useHandCursor: true });

      btn.on('pointerdown', () => {
        this.currentLayer = layer;
        this.refreshUI();
      });

      this.uiContainer.add(btn);
    });

    // Show different palette based on layer
    if (this.currentLayer === 'objects') {
      this.createObjectPalette();
    } else {
      this.createTilePalette();
    }

    // Collision overlay toggle
    const collisionToggle = this.add.text(95, height - 220, this.showCollisionOverlay ? '[ HIDE COLLISION ]' : '[ SHOW COLLISION ]', {
      fontFamily: 'monospace',
      fontSize: '11px',
      color: this.showCollisionOverlay ? '#ef4444' : '#888888',
      backgroundColor: '#333333',
      padding: { x: 8, y: 6 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    collisionToggle.on('pointerover', () => collisionToggle.setStyle({ backgroundColor: '#444444' }));
    collisionToggle.on('pointerout', () => collisionToggle.setStyle({ backgroundColor: '#333333' }));
    collisionToggle.on('pointerdown', () => {
      this.showCollisionOverlay = !this.showCollisionOverlay;
      this.renderMap();
      this.refreshUI();
    });
    this.uiContainer.add(collisionToggle);

    // Save button
    const saveBtn = this.add.text(95, height - 180, '[ SAVE MAP ]', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#ffffff',
      backgroundColor: '#166534',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    saveBtn.on('pointerover', () => saveBtn.setStyle({ backgroundColor: '#22c55e' }));
    saveBtn.on('pointerout', () => saveBtn.setStyle({ backgroundColor: '#166534' }));
    saveBtn.on('pointerdown', () => this.saveMap());
    this.uiContainer.add(saveBtn);

    // Copy JSON button
    const copyBtn = this.add.text(95, height - 140, '[ COPY JSON ]', {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#ffffff',
      backgroundColor: '#1e3a5f',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    copyBtn.on('pointerover', () => copyBtn.setStyle({ backgroundColor: '#2d5a87' }));
    copyBtn.on('pointerout', () => copyBtn.setStyle({ backgroundColor: '#1e3a5f' }));
    copyBtn.on('pointerdown', () => this.copyMapToClipboard());
    this.uiContainer.add(copyBtn);

    // Back button
    const backBtn = this.add.text(95, height - 120, '[ BACK TO MENU ]', {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#888888',
      padding: { x: 10, y: 4 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    backBtn.on('pointerover', () => backBtn.setStyle({ color: '#ffffff' }));
    backBtn.on('pointerout', () => backBtn.setStyle({ color: '#888888' }));
    backBtn.on('pointerdown', () => this.scene.start('MainMenuScene'));
    this.uiContainer.add(backBtn);
  }

  private createTilePalette(): void {
    // Use different tile sets for different layers
    const tiles = this.currentLayer === 'collision' ? this.collisionTiles : this.visualTiles;

    // Layer description
    const desc = this.currentLayer === 'collision'
      ? 'Collision layer:\nDefines what blocks\nmovement (invisible)'
      : 'Visual tiles:\nAppearance only,\nno collision effect';

    const descText = this.add.text(10, 95, desc, {
      fontFamily: 'monospace',
      fontSize: '10px',
      color: '#888888',
    });
    this.uiContainer.add(descText);

    // Tile buttons
    tiles.forEach((tile, i) => {
      const y = 155 + i * 45;

      // Tile preview
      const preview = this.add.graphics();
      this.drawIsometricTileSmall(preview, 20, y + 15, tile.color);
      this.uiContainer.add(preview);

      // Tile button
      const btn = this.add.text(55, y + 10, tile.name, {
        fontFamily: 'monospace',
        fontSize: '12px',
        color: this.selectedTile === tile.id ? '#4ade80' : '#ffffff',
        backgroundColor: this.selectedTile === tile.id ? '#2d5a47' : '#1e3a5f',
        padding: { x: 8, y: 4 },
      }).setInteractive({ useHandCursor: true });

      btn.on('pointerdown', () => {
        this.selectedTile = tile.id;
        this.refreshUI();
      });

      this.uiContainer.add(btn);
    });
  }

  private createObjectPalette(): void {
    const objectTypes: { type: 'player_spawn' | 'shrine' | 'delete'; name: string; color: number }[] = [
      { type: 'player_spawn', name: 'Player Spawn', color: 0x3b82f6 },
      { type: 'shrine', name: 'Shrine', color: 0x22c55e },
      { type: 'delete', name: 'Delete Object', color: 0xef4444 },
    ];

    objectTypes.forEach((obj, i) => {
      const y = 100 + i * 50;

      // Object preview (circle marker)
      const preview = this.add.graphics();
      preview.fillStyle(obj.color, 0.8);
      preview.fillCircle(20, y + 15, 10);
      preview.lineStyle(2, 0xffffff, 0.5);
      preview.strokeCircle(20, y + 15, 10);
      this.uiContainer.add(preview);

      // Object button
      const btn = this.add.text(40, y + 10, obj.name, {
        fontFamily: 'monospace',
        fontSize: '11px',
        color: this.selectedObjectType === obj.type ? '#4ade80' : '#ffffff',
        backgroundColor: this.selectedObjectType === obj.type ? '#2d5a47' : '#1e3a5f',
        padding: { x: 6, y: 4 },
      }).setInteractive({ useHandCursor: true });

      btn.on('pointerdown', () => {
        this.selectedObjectType = obj.type;
        this.refreshUI();
      });

      this.uiContainer.add(btn);
    });

    // Show placed objects count
    const shrineCount = this.mapObjects.filter(o => o.type === 'revival_shrine').length;
    const spawnCount = this.mapObjects.filter(o => o.type === 'player_spawn').length;

    const countText = this.add.text(10, 270, `Spawns: ${spawnCount}\nShrines: ${shrineCount}`, {
      fontFamily: 'monospace',
      fontSize: '11px',
      color: '#888888',
    });
    this.uiContainer.add(countText);
  }

  private refreshUI(): void {
    this.uiContainer.destroy();
    this.createUI();
  }

  private setupInput(): void {
    // Mouse/touch input for tile placement (left click) and panning (right/middle click)
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.x > this.cameras.main.width - 200) return; // Ignore UI area

      // Right click or middle click for panning
      if (pointer.rightButtonDown() || pointer.middleButtonDown()) {
        this.isPanning = true;
        this.lastPanPoint = { x: pointer.x, y: pointer.y };
        return;
      }

      // Left click for painting
      this.isDragging = true;
      this.placeTileAtPointer(pointer);
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      // Handle panning
      if (this.isPanning) {
        const deltaX = pointer.x - this.lastPanPoint.x;
        const deltaY = pointer.y - this.lastPanPoint.y;

        // Move graphics position for panning
        this.tileGraphics.x += deltaX;
        this.tileGraphics.y += deltaY;
        this.objectGraphics.x += deltaX;
        this.objectGraphics.y += deltaY;
        this.cursorGraphics.x += deltaX;
        this.cursorGraphics.y += deltaY;

        this.lastPanPoint = { x: pointer.x, y: pointer.y };
        return;
      }

      this.updateCursor(pointer);

      if (this.isDragging && pointer.x < this.cameras.main.width - 200) {
        this.placeTileAtPointer(pointer);
      }
    });

    this.input.on('pointerup', () => {
      this.isDragging = false;
      this.isPanning = false;
      this.lastDragTile = { x: -1, y: -1 };
    });

    // Disable context menu for right-click panning
    this.input.mouse?.disableContextMenu();

    // Keyboard shortcuts
    this.input.keyboard?.on('keydown-ONE', () => { this.selectedTile = 0; this.refreshUI(); });
    this.input.keyboard?.on('keydown-TWO', () => { this.selectedTile = 1; this.refreshUI(); });
    this.input.keyboard?.on('keydown-THREE', () => { this.selectedTile = 2; this.refreshUI(); });
    this.input.keyboard?.on('keydown-FOUR', () => { this.selectedTile = 3; this.refreshUI(); });
    this.input.keyboard?.on('keydown-FIVE', () => { this.selectedTile = 4; this.refreshUI(); });

    this.input.keyboard?.on('keydown-G', () => { this.currentLayer = 'ground'; this.refreshUI(); });
    this.input.keyboard?.on('keydown-P', () => { this.currentLayer = 'paths'; this.refreshUI(); });
    this.input.keyboard?.on('keydown-C', () => { this.currentLayer = 'collision'; this.refreshUI(); });
    this.input.keyboard?.on('keydown-O', () => { this.currentLayer = 'objects'; this.refreshUI(); });

    this.input.keyboard?.on('keydown-S', (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        this.saveMap();
      }
    });

    // Scroll to zoom (only affects map, not UI)
    this.input.on('wheel', (pointer: Phaser.Input.Pointer, _gameObjects: Phaser.GameObjects.GameObject[], _deltaX: number, deltaY: number) => {
      // Don't zoom if over UI
      if (pointer.x > this.cameras.main.width - 200) return;

      const oldZoom = this.zoomLevel;

      if (deltaY < 0) {
        this.zoomLevel = Math.min(3, this.zoomLevel + 0.1);
      } else {
        this.zoomLevel = Math.max(0.3, this.zoomLevel - 0.1);
      }

      if (oldZoom !== this.zoomLevel) {
        // Get current position before zoom
        const oldX = this.tileGraphics.x;
        const oldY = this.tileGraphics.y;

        // Calculate zoom factor
        const zoomFactor = this.zoomLevel / oldZoom;

        // Zoom toward pointer position
        const newX = pointer.x - (pointer.x - oldX) * zoomFactor;
        const newY = pointer.y - (pointer.y - oldY) * zoomFactor;

        // Apply scale and position to all graphics
        this.tileGraphics.setScale(this.zoomLevel);
        this.tileGraphics.setPosition(newX, newY);

        this.objectGraphics.setScale(this.zoomLevel);
        this.objectGraphics.setPosition(newX, newY);

        this.cursorGraphics.setScale(this.zoomLevel);
        this.cursorGraphics.setPosition(newX, newY);
      }
    });
  }

  private placeTileAtPointer(pointer: Phaser.Input.Pointer): void {
    const tile = this.screenToTile(pointer.x, pointer.y);

    if (tile.x < 0 || tile.y < 0 || tile.x >= this.mapWidth || tile.y >= this.mapHeight) {
      return;
    }

    // Don't re-place on same tile during drag
    if (tile.x === this.lastDragTile.x && tile.y === this.lastDragTile.y) {
      return;
    }

    this.lastDragTile = { x: tile.x, y: tile.y };

    // Handle objects layer differently
    if (this.currentLayer === 'objects') {
      this.handleObjectPlacement(tile.x, tile.y);
      return;
    }

    const index = tile.y * this.mapWidth + tile.x;
    this.mapData[this.currentLayer][index] = this.selectedTile;

    this.renderMap();
  }

  private handleObjectPlacement(tileX: number, tileY: number): void {
    // Check if there's already an object at this location
    const existingIndex = this.mapObjects.findIndex(
      obj => Math.floor(obj.x) === tileX && Math.floor(obj.y) === tileY
    );

    if (this.selectedObjectType === 'delete') {
      // Delete object if one exists here
      if (existingIndex !== -1) {
        this.mapObjects.splice(existingIndex, 1);
        this.renderObjects();
        this.refreshUI();
      }
      return;
    }

    // Don't place if object already exists at this location
    if (existingIndex !== -1) {
      return;
    }

    // Determine object type for Tiled
    const tiledType = this.selectedObjectType === 'shrine' ? 'revival_shrine' : 'player_spawn';
    const name = this.selectedObjectType === 'shrine'
      ? `shrine_${this.mapObjects.filter(o => o.type === 'revival_shrine').length + 1}`
      : 'player_spawn';

    // Create new object
    const newObject: MapObject = {
      id: this.nextObjectId++,
      name,
      type: tiledType,
      x: tileX,
      y: tileY,
      width: 1,
      height: 1,
    };

    this.mapObjects.push(newObject);
    this.renderObjects();
    this.refreshUI();
  }

  private renderObjects(): void {
    this.objectGraphics.clear();

    for (const obj of this.mapObjects) {
      const screen = worldToScreen(obj.x, obj.y);

      // Choose color based on type
      let color = 0x888888;

      if (obj.type === 'player_spawn') {
        color = 0x3b82f6; // Blue
      } else if (obj.type === 'revival_shrine') {
        color = 0x22c55e; // Green
      }

      // Draw marker
      const centerX = screen.x;
      const centerY = screen.y + TILE_HEIGHT / 2;

      // Outer glow
      this.objectGraphics.fillStyle(color, 0.3);
      this.objectGraphics.fillCircle(centerX, centerY, 20);

      // Inner circle
      this.objectGraphics.fillStyle(color, 0.8);
      this.objectGraphics.fillCircle(centerX, centerY, 12);

      // Border
      this.objectGraphics.lineStyle(2, 0xffffff, 0.8);
      this.objectGraphics.strokeCircle(centerX, centerY, 12);

      // Label (using graphics since we're in local coords)
      // We'll add text separately since graphics can't render text
    }

    // Re-render object labels as text objects attached to objectGraphics position
    // For simplicity, just highlight objects - labels would need separate handling
  }

  private screenToTile(screenX: number, screenY: number): { x: number; y: number } {
    // Account for graphics position and scale
    const graphicsPos = { x: this.tileGraphics.x, y: this.tileGraphics.y };

    // Convert screen position to map-local position
    const localX = (screenX - graphicsPos.x) / this.zoomLevel;
    const localY = (screenY - graphicsPos.y) / this.zoomLevel;

    // Convert to world coordinates
    const world = screenToWorld(localX, localY);

    return {
      x: Math.floor(world.x),
      y: Math.floor(world.y),
    };
  }

  private updateCursor(pointer: Phaser.Input.Pointer): void {
    this.cursorGraphics.clear();

    // Check if pointer is over UI (use screen coordinates)
    if (pointer.x > this.cameras.main.width - 200) return;

    const tile = this.screenToTile(pointer.x, pointer.y);

    if (tile.x < 0 || tile.y < 0 || tile.x >= this.mapWidth || tile.y >= this.mapHeight) {
      return;
    }

    // Convert tile to local graphics coordinates (before scaling)
    const screen = worldToScreen(tile.x, tile.y);
    const x = screen.x;
    const y = screen.y;

    // Draw cursor highlight in local coordinates (graphics scaling handles zoom)
    this.cursorGraphics.lineStyle(2 / this.zoomLevel, 0xffffff, 1);
    this.cursorGraphics.beginPath();
    this.cursorGraphics.moveTo(x, y);
    this.cursorGraphics.lineTo(x + TILE_WIDTH / 2, y + TILE_HEIGHT / 2);
    this.cursorGraphics.lineTo(x, y + TILE_HEIGHT);
    this.cursorGraphics.lineTo(x - TILE_WIDTH / 2, y + TILE_HEIGHT / 2);
    this.cursorGraphics.closePath();
    this.cursorGraphics.strokePath();
  }

  private renderMap(): void {
    this.tileGraphics.clear();

    // Render in isometric order (back to front)
    // Draw in local coordinates (graphics position handles offset)
    for (let y = 0; y < this.mapHeight; y++) {
      for (let x = 0; x < this.mapWidth; x++) {
        const index = y * this.mapWidth + x;

        // Get tile from current layer, fall back to ground
        let tileId = this.mapData.ground[index];

        // Render paths on top of ground
        if (this.mapData.paths[index] > 0) {
          tileId = this.mapData.paths[index];
        }

        const tileInfo = this.visualTiles.find(t => t.id === tileId) || this.visualTiles[0];
        const screen = worldToScreen(x, y);

        this.drawIsometricTile(
          this.tileGraphics,
          screen.x,
          screen.y,
          tileInfo.color,
          1
        );

        // Overlay collision layer when enabled (more opaque when editing collision)
        if (this.showCollisionOverlay && this.mapData.collision[index] > 0) {
          const collisionTileId = this.mapData.collision[index];
          const collisionInfo = this.collisionTiles.find(t => t.id === collisionTileId) || this.collisionTiles[0];
          const opacity = this.currentLayer === 'collision' ? 0.7 : 0.4;
          this.drawIsometricTile(
            this.tileGraphics,
            screen.x,
            screen.y,
            collisionInfo.color,
            opacity
          );
        }
      }
    }
  }

  private drawIsometricTile(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    color: number,
    alpha: number = 1
  ): void {
    graphics.fillStyle(color, alpha);
    graphics.lineStyle(1, 0x000000, 0.3);

    graphics.beginPath();
    graphics.moveTo(x, y);
    graphics.lineTo(x + TILE_WIDTH / 2, y + TILE_HEIGHT / 2);
    graphics.lineTo(x, y + TILE_HEIGHT);
    graphics.lineTo(x - TILE_WIDTH / 2, y + TILE_HEIGHT / 2);
    graphics.closePath();
    graphics.fillPath();
    graphics.strokePath();
  }

  private drawIsometricTileSmall(graphics: Phaser.GameObjects.Graphics, x: number, y: number, color: number): void {
    const w = 24;
    const h = 12;

    graphics.fillStyle(color, 1);
    graphics.lineStyle(1, 0x000000, 0.5);

    graphics.beginPath();
    graphics.moveTo(x, y - h / 2);
    graphics.lineTo(x + w / 2, y);
    graphics.lineTo(x, y + h / 2);
    graphics.lineTo(x - w / 2, y);
    graphics.closePath();
    graphics.fillPath();
    graphics.strokePath();
  }

  private generateMapJson(): string {
    const mapJson = {
      compressionlevel: -1,
      height: this.mapHeight,
      width: this.mapWidth,
      infinite: false,
      orientation: 'isometric',
      renderorder: 'right-down',
      tileheight: 32,
      tilewidth: 64,
      tiledversion: '1.10.0',
      type: 'map',
      version: '1.10',
      tilesets: [
        {
          firstgid: 1,
          name: 'terrain',
          tilewidth: 64,
          tileheight: 32,
          tilecount: 4,
          columns: 4,
          image: '../images/tilesets/terrain_placeholder.png',
          imageheight: 32,
          imagewidth: 256,
        },
      ],
      layers: [
        {
          id: 1,
          name: 'ground',
          type: 'tilelayer',
          visible: true,
          width: this.mapWidth,
          height: this.mapHeight,
          x: 0,
          y: 0,
          opacity: 1,
          data: this.mapData.ground,
        },
        {
          id: 2,
          name: 'paths',
          type: 'tilelayer',
          visible: true,
          width: this.mapWidth,
          height: this.mapHeight,
          x: 0,
          y: 0,
          opacity: 1,
          data: this.mapData.paths,
        },
        {
          id: 3,
          name: 'collision',
          type: 'tilelayer',
          visible: false,
          width: this.mapWidth,
          height: this.mapHeight,
          x: 0,
          y: 0,
          opacity: 1,
          data: this.mapData.collision,
        },
        {
          id: 4,
          name: 'spawns',
          type: 'objectgroup',
          visible: true,
          x: 0,
          y: 0,
          opacity: 1,
          objects: this.generateObjectsArray(),
        },
      ],
    };

    return JSON.stringify(mapJson, null, 2);
  }

  private generateObjectsArray(): object[] {
    // Ensure there's always a player spawn
    const hasPlayerSpawn = this.mapObjects.some(obj => obj.type === 'player_spawn');

    const objects: object[] = [];

    // Add default player spawn if none exists
    if (!hasPlayerSpawn) {
      objects.push({
        id: 1,
        name: 'player_spawn',
        type: 'player_spawn',
        x: 0,
        y: 320, // Default spawn at center (world 10, 10)
        width: 0,
        height: 0,
        properties: [],
      });
    }

    // Add all placed objects
    for (const obj of this.mapObjects) {
      const tiledCoords = this.worldToTiledPixel(obj.x, obj.y);

      objects.push({
        id: obj.id,
        name: obj.name,
        type: obj.type,
        x: tiledCoords.x,
        y: tiledCoords.y,
        width: obj.type === 'revival_shrine' ? 64 : 0,
        height: obj.type === 'revival_shrine' ? 32 : 0,
        properties: [],
      });
    }

    return objects;
  }

  private saveMap(): void {
    const json = this.generateMapJson();

    // Create download
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'meadowvale_test.json';
    a.click();
    URL.revokeObjectURL(url);

    // Show confirmation
    const msg = this.add.text(
      this.cameras.main.width / 2,
      50,
      'Map saved! Check your downloads folder.',
      {
        fontFamily: 'monospace',
        fontSize: '16px',
        color: '#4ade80',
        backgroundColor: '#1a1a2e',
        padding: { x: 10, y: 5 },
      }
    ).setOrigin(0.5);

    this.time.delayedCall(2000, () => msg.destroy());
  }

  private copyMapToClipboard(): void {
    const json = this.generateMapJson();

    navigator.clipboard.writeText(json).then(() => {
      const msg = this.add.text(
        this.cameras.main.width / 2,
        50,
        'Map JSON copied to clipboard!',
        {
          fontFamily: 'monospace',
          fontSize: '16px',
          color: '#4ade80',
          backgroundColor: '#1a1a2e',
          padding: { x: 10, y: 5 },
        }
      ).setOrigin(0.5);

      this.time.delayedCall(2000, () => msg.destroy());
    });
  }
}
